import React, { useState, useEffect } from 'react';
import Collapse from '../Common/Collapse';
import { getSections, createSection, updateSection, deleteSection, reorderSections } from '../../services/adminService';
import LessonsManager from './LessonsManager';
import Modal from '../Common/Modal';
import Input from '../Common/Input';
import Button from '../Common/Button';
import { confirmDialog } from '../Common/confirm';
import DragHandle from '../Common/DragHandle';
import useDragSort from '../../hooks/useDragSort';
import toast from 'react-hot-toast';

const IcoLayers = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 2 7 12 12 22 7 12 2"/>
    <polyline points="2 17 12 22 22 17"/>
    <polyline points="2 12 12 17 22 12"/>
  </svg>
);

const IcoEdit = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);

const IcoTrash = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/>
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
    <path d="M10 11v6"/><path d="M14 11v6"/>
  </svg>
);

const IcoChevron = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6"/>
  </svg>
);

const IcoPlus = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);

const SectionsManager = ({ courseId }) => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [formData, setFormData] = useState({ name: '' });
  const [expandedSections, setExpandedSections] = useState({});

  useEffect(() => { loadSections(); }, [courseId]);

  const loadSections = async () => {
    try {
      setLoading(true);
      const response = await getSections(courseId);
      setSections(response.data || []);
    } catch {
      toast.error('Ошибка загрузки разделов');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingSection(null);
    setFormData({ name: '' });
    setModalOpen(true);
  };

  const handleEdit = (section) => {
    setEditingSection(section);
    setFormData({ name: section.name });
    setModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (await confirmDialog({ title: 'Удалить раздел?', message: 'Все уроки внутри будут удалены. Действие необратимо.', confirmText: 'Удалить' })) {
      try {
        await deleteSection(id);
        toast.success('Раздел удален');
        loadSections();
      } catch {
        toast.error('Ошибка при удалении');
      }
    }
  };

  const handleSubmit = async () => {
    if (!formData.name) { toast.error('Введите название раздела'); return; }
    try {
      if (editingSection) {
        await updateSection(editingSection.id, { name: formData.name });
        toast.success('Раздел обновлен');
      } else {
        await createSection({ name: formData.name, courseId });
        toast.success('Раздел создан');
      }
      setModalOpen(false);
      loadSections();
    } catch {
      toast.error('Ошибка при сохранении');
    }
  };

  const toggleSection = (id) =>
    setExpandedSections(prev => ({ ...prev, [id]: !prev[id] }));

  // Перетаскивание модулей внутри курса (за ручку, с анимацией)
  const { getHandleProps, getItemProps } = useDragSort(
    sections,
    (ids) => setSections(prev => ids.map(id => prev.find(s => s.id === id)).filter(Boolean)),
    async (ids) => { try { await reorderSections(ids); } catch { toast.error('Не удалось сохранить порядок'); loadSections(); } }
  );

  if (loading) return <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--text-3)', fontSize: 14 }}>Загрузка...</div>;

  return (
    <div className="sec-acc">
      {sections.map((section) => {
        const open = !!expandedSections[section.id];
        const count = section.Lessons?.length || 0;
        return (
          <div key={section.id} className={`acc${open ? ' open' : ''}`} {...getItemProps(section.id)}>
            <div className="acc-head" onClick={() => toggleSection(section.id)}>
              <div className="acc-ic"><IcoLayers /></div>
              <div className="acc-info">
                <div className="acc-title">{section.name}</div>
                <div className="acc-sub">{count} {count === 1 ? 'урок' : count < 5 ? 'урока' : 'уроков'}</div>
              </div>
              <span className="acc-count">{count}</span>
              <div className="acc-actions" onClick={e => e.stopPropagation()}>
                <button className="acc-btn" title="Изменить" onClick={() => handleEdit(section)}><IcoEdit /></button>
                <button className="acc-btn del" title="Удалить" onClick={() => handleDelete(section.id)}><IcoTrash /></button>
                <DragHandle {...getHandleProps(section.id)} />
              </div>
              <span className="acc-chev"><IcoChevron /></span>
            </div>

            <Collapse open={open}>
              <div className="acc-body">
                <LessonsManager sectionId={section.id} onUpdate={loadSections} />
              </div>
            </Collapse>
          </div>
        );
      })}

      <button className="acc-add" onClick={handleAdd}>
        <IcoPlus /> Добавить раздел
      </button>

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={editingSection ? 'Редактировать раздел' : 'Новый раздел'}>
        <Input
          label="Название раздела"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          required
        />
        <div className="form-actions">
          <Button variant="secondary" onClick={() => setModalOpen(false)}>Отмена</Button>
          <Button variant="purple" onClick={handleSubmit}>Сохранить</Button>
        </div>
      </Modal>
    </div>
  );
};

export default SectionsManager;
